import {
    SimpleForm,
    useTranslate,
    ReferenceInput,
    AutocompleteInput,
    required,
    BooleanInput,
    NumberInput,
    minValue,
    maxValue,
    FormDataConsumer,
} from 'react-admin';
import { Box } from '@mui/material';
import { deviceFilterToQuery } from '@/helpers/filters/filterToQuery.js';
import { useCallback, useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Palette } from '@/themes/themeOverrides/Palette.js';
import { EditToolbar } from '@/components/global/buttons/toolbar.jsx';
import { DateInput } from '@/components/global/inputs/DateInput.jsx';
import { validateDaysToRotateUtil, validateExpiresAfterStartUtil, validateNotPastDateUtil } from '@/components/accessKeys/validators/validators.jsx';

const ClearDaysToRotateWhenAutoRotationOff = () => {
    const autoRotation = useWatch({ name: 'autoRotation' });
    const { unregister } = useFormContext();

    useEffect(() => {
        if (autoRotation === false) {
            unregister('daysToRotate');
        }
    }, [autoRotation, unregister]);

    return null;
};

export const AccessKeyForm = () => {
    const translate = useTranslate();
    const validateExpiresAfterStart = useCallback((value, allValues) => validateExpiresAfterStartUtil(value, allValues, translate), [translate]);
    const validateNotPastDate = useCallback(value => validateNotPastDateUtil(value, translate), [translate]);
    const validateDaysToRotate = useCallback((value, allValues) => validateDaysToRotateUtil(value, allValues, translate), [translate]);

    return (
        <SimpleForm
            toolbar={<EditToolbar alwaysEnableSave label={translate('common.save')} icon={null} sx={{ marginTop: '32px' }} />}
            sx={{
                '& .autocomplete-parent': {
                    '& .ra-input-type': {
                        '& .MuiFormLabel-root': {
                            color: `${Palette.gray.gray400} !important`,
                        },
                        '& .MuiInputBase-root.Mui-disabled': {
                            backgroundColor: Palette.white,
                            '& .MuiSelect-select': {
                                fontWeight: '400 !important',
                                WebkitTextFillColor: `${Palette.gray.gray400} !important`,
                            },
                        },
                    },
                },
                '& .MuiInputLabel-root.MuiFormLabel-filled': {
                    color: `${Palette.gray.gray900} !important`,
                },
                '& .MuiAutocomplete-popper': {
                    backgroundColor: Palette.white,
                    padding: '4px 0',
                    boxShadow: Palette.boxShadow.elevation8,
                    borderRadius: '12px',
                    '& ul': {
                        padding: 0,
                    },
                    '& .MuiAutocomplete-noOptions': {
                        margin: '0 16px',
                    },
                },
                '& .rs-picker.rs-picker-date': {
                    marginBottom: '24px !important',
                },
            }}
        >
            <ClearDaysToRotateWhenAutoRotationOff />
            <Box className='autocomplete-parent'>
                <ReferenceInput source='deviceId' reference='device' perPage={25} name='deviceId' filter={{ 'accessKey.id': null, withAggregates: true }}>
                    <AutocompleteInput
                        filterToQuery={deviceFilterToQuery}
                        label={translate('resources.accessKey.labels.device')}
                        validate={required(translate('common.validation.required'))}
                        fullWidth
                        noOptionsText={translate('resources.accessKey.labels.noDeviceFound')}
                        disablePortal={true}
                    />
                </ReferenceInput>
            </Box>
            <DateInput
                className='date-input'
                name='currentKeyValidFrom'
                source='currentKeyValidFrom'
                label={translate('resources.accessKey.labels.currentKeyValidFrom')}
                fullWidth
                validate={[required(translate('common.validation.required')), validateNotPastDate]}
                defaultValue={new Date().toISOString().split('T')[0]}
            />
            <DateInput
                className='date-input'
                name='currentKeyExpires'
                source='currentKeyExpires'
                label={translate('resources.accessKey.labels.currentKeyExpires')}
                fullWidth
                validate={[validateNotPastDate, validateExpiresAfterStart]}
                defaultValue={new Date(Date.now() + 35 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
            />
            <BooleanInput
                label={translate('resources.accessKey.labels.autoRotation')}
                source='autoRotation'
                name='autoRotation'
                defaultValue={true}
                fullWidth
                size='small'
                sx={{
                    '& .MuiFormControlLabel-root': {
                        margin: '0',
                    },
                }}
            />
            <FormDataConsumer>
                {({ formData }) =>
                    formData.autoRotation ? (
                        <NumberInput
                            label={translate('resources.accessKey.labels.daysToRotate')}
                            name='daysToRotate'
                            source='daysToRotate'
                            fullWidth
                            validate={[minValue(5), maxValue(365), required(translate('common.validation.required')), validateDaysToRotate]}
                            defaultValue={30}
                        />
                    ) : null
                }
            </FormDataConsumer>
        </SimpleForm>
    );
};
